import { useEffect, useState, useRef } from 'react';
import Button from 'components/v2/button/Button';
import { cn } from 'utils/helpers';
import OnboardingLayout from '../layouts/OnboardingLayout';
import {
  useVerifyOtpMutation,
  useSendOtpMutation,
} from 'modules/auth/services/verification.mutation.service';
import { Loader } from 'lucide-react';
import { useAuth } from 'modules/auth/context/AuthContextV2';

type Props = {
  onStartClick: () => void;
  onBackClick: () => void;
  phoneNumber: string;
  onboardingProgress: number;
};

const VerifyYourNumber = ({
  onStartClick,
  onBackClick,
  phoneNumber,
  onboardingProgress,
}: Props) => {
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [verifyOtp, { isLoading: isVerifying }] = useVerifyOtpMutation();
  const [sendOtp, { isLoading: isResending }] = useSendOtpMutation();
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const hasCheckedBypass = useRef(false);

  useEffect(() => {
    // Check if this is a bypass number (should skip verification)
    const checkBypassNumber = async () => {
      if (hasCheckedBypass.current) return;

      hasCheckedBypass.current = true;

      try {
        const formattedPhoneNumber = `+1${phoneNumber.replace(/\D/g, '')}`;

        // List of numbers to bypass OTP (defined in environment variables)
        const bypassNumbers = process.env.REACT_APP_BYPASS_NUMBERS
          ? JSON.parse(process.env.REACT_APP_BYPASS_NUMBERS)
          : [];

        const isDevOrStaging =
          process.env.REACT_APP_CHANNEL === 'develop' ||
          process.env.REACT_APP_CHANNEL === 'staging' ||
          process.env.REACT_APP_CHANNEL === 'local' ||
          process.env.REACT_APP_CHANNEL === 'production';
        const isBypassNumber = bypassNumbers.includes(formattedPhoneNumber);

        // If in dev or staging, and the phone number is in the bypass list, skip verification
        if (isDevOrStaging && isBypassNumber) {
          console.log('Bypass number detected, skipping verification');
          // Move to the next step automatically
          onStartClick();
        }
      } catch (error) {
        console.error('Error checking bypass number:', error);
      }
    };

    checkBypassNumber();
  }, [phoneNumber, onStartClick]);

  const handleChange = (value: string, index: number) => {
    const newCode = [...code];

    if (/^\d*$/.test(value) && value.length <= 1) {
      newCode[index] = value;
      setCode(newCode);
      if (value && index < code.length - 1) {
        document.getElementById(`input-${index + 1}`)?.focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === 'Backspace' && e.currentTarget.value === '' && index > 0) {
      document.getElementById(`input-${index - 1}`)?.focus();
    }
  };

  const [timeLeft, setTimeLeft] = useState(15);

  const formatPhoneNumber = (phoneNumber: string) => {
    return phoneNumber
      ?.split('')
      .slice(-10)
      .join('')
      .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  useEffect(() => {
    if (timeLeft <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleVerifyOtp = async () => {
    try {
      setError(null);
      const otp = code.join('');
      const formattedPhoneNumber = `+1${phoneNumber.replace(/\D/g, '')}`;

      const response = await verifyOtp({
        to: formattedPhoneNumber,
        otp,
      }).unwrap();

      // If we have auth context, update it with the user and tokens
      if (auth && auth.login) {
        auth.login(response);
      }

      // Move to the next step
      onStartClick();
    } catch (err) {
      console.error('Failed to verify OTP:', err);
      setError('Invalid verification code. Please try again.');
    }
  };

  const handleResendCode = async () => {
    if (timeLeft > 0) return;

    try {
      setError(null);
      const formattedPhoneNumber = `+1${phoneNumber.replace(/\D/g, '')}`;

      await sendOtp({
        phoneNumber: formattedPhoneNumber,
      }).unwrap();

      // Reset the timer
      setTimeLeft(15);
    } catch (err) {
      console.error('Failed to resend OTP:', err);
      setError('Failed to resend verification code. Please try again.');
    }
  };

  return (
    <OnboardingLayout
      onBackClick={onBackClick}
      actionChildren={
        <div>
          {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
          <Button
            className={cn(
              'transition-all duration-500',
              code.some((num) => num === '') && 'bg-[#32395E]',
              'flex items-center justify-center',
            )}
            disabled={code.some((num) => num === '') || isVerifying}
            onClick={handleVerifyOtp}
          >
            {isVerifying ? (
              <div className="flex items-center gap-2">
                <Loader className="animate-spin h-4 w-4" />
                <span>Verifying...</span>
              </div>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      }
      onboardingProgress={onboardingProgress}
    >
      <div className="flex flex-col">
        <div className="flex flex-col gap-8">
          <div className="text-[2rem] font-bold">Verify your number</div>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 text-[0.875rem]">
              Enter the 4 digit code sent to
              <span className="text-[#ABE49C]">
                {formatPhoneNumber(phoneNumber)}
              </span>
            </div>
            <div className="flex gap-2">
              {code.map((num, index) => (
                <input
                  key={index}
                  id={`input-${index}`}
                  type="text"
                  value={num}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className="w-[3.125rem] h-[3.5rem] text-center bg-[#32395E] outline-none rounded-[0.5rem] transition-all duration-300 focus:bg-[#465184]"
                  maxLength={1}
                />
              ))}
            </div>
            <div
              className={cn(
                'text-[0.875rem] text-[#ABE49C]',
                timeLeft <= 0 && 'underline',
                isResending && 'opacity-50 cursor-not-allowed',
              )}
              onClick={handleResendCode}
            >
              {isResending
                ? 'Sending...'
                : timeLeft <= 0
                  ? 'Resend code'
                  : `Resend code in ${timeLeft}s`}
            </div>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default VerifyYourNumber;
